import { PENDING_TRANSACTION_STATUSES } from '~/constants/smart_accounts'
import { useActiveOrg } from '~/hooks/org'
import { useTransactions } from '~/hooks/useTransactions'
import { OrgMerkleTransaction, OrgTransaction } from '~/types'

type UsePendingMerkleTransactionsReturn = {
  hasPendingMerkleTransaction: boolean
  pendingTransactions: OrgMerkleTransaction[]
  isLoading: boolean
}

/**
 * Hook to fetch and track unsubmitted merkle transactions for a specific smart account.
 * Used to prevent account changes when others are still pending.
 */
const usePendingMerkleTransactions = ({
  enabled = true,
}: { enabled?: boolean } = {}): UsePendingMerkleTransactionsReturn => {
  const { org } = useActiveOrg()
  const { transactions, isInitialLoading } = useTransactions({
    smartAccountOrgId: org?.id,
    enabled,
  })

  if (!transactions) {
    return {
      hasPendingMerkleTransaction: false,
      pendingTransactions: [],
      isLoading: isInitialLoading,
    }
  }

  const pendingMerkleTransactions = transactions
    // have to do this filter first for typing reasons
    .filter(
      (transaction): transaction is OrgTransaction =>
        transaction.isSplitsTransaction,
    )
    .filter(
      (transaction): transaction is OrgMerkleTransaction =>
        transaction.isMerkleTransaction,
    )
    .filter((transaction) =>
      PENDING_TRANSACTION_STATUSES.includes(transaction.status),
    )

  return {
    hasPendingMerkleTransaction: pendingMerkleTransactions.length > 0,
    pendingTransactions: pendingMerkleTransactions,
    isLoading: isInitialLoading,
  }
}

export const useHasPendingMerkleTransactionsForSmartAccountId = (
  smartAccountId: string,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { pendingTransactions, isLoading } = usePendingMerkleTransactions({
    enabled,
  })
  return {
    hasPendingTransactions: pendingTransactions.some(
      (transaction) => transaction.smartAccountId === smartAccountId,
    ),
    isLoading,
  }
}
